import saveSortAndFilter from "@ext/markdown/elements/table/edit/logic/sortAndFilter/saveSortAndFilter";
import {
	getSaved,
	restoreOrder,
	sortRows,
} from "@ext/markdown/elements/table/edit/logic/sortAndFilter/sortFilterUtils";
import getTableData from "@ext/markdown/elements/table/edit/logic/tableData/getTableData";
import type {
	FilterAndSort,
	FilterAndSortProps,
	FilterState,
	SortRecord,
	SortState,
} from "@ext/markdown/elements/table/edit/model/tableTypes";
import {
	getColumnsValues,
	getFilterChange,
	useWatchSort,
} from "@ext/markdown/elements/table/render/components/FilterAndSort/sortAndFilterReact";
import { hasActiveEntries } from "@ext/markdown/elements/table/render/logic/sortFilterUtilsRender";
import type { Editor } from "@tiptap/core";
import type { Node } from "@tiptap/pm/model";
import { useCallback, useLayoutEffect, useMemo, useState } from "react";

const canSortTable = (node: Node) => {
	for (let rowIndex = 0; rowIndex < node.childCount; rowIndex++) {
		const row = node.child(rowIndex);

		for (let cellIndex = 0; cellIndex < row.childCount; cellIndex++) {
			const cell = row.child(cellIndex);
			if ((cell.attrs.colspan && cell.attrs.colspan !== 1) || (cell.attrs.rowspan && cell.attrs.rowspan !== 1)) {
				return false;
			}
		}
	}

	return true;
};

const keepIfEqual = <T>(prev: T, next: T): T => (JSON.stringify(prev) === JSON.stringify(next) ? prev : next);

const useFilterAndSort = (node: Node, editor: Editor, pos: number): FilterAndSortProps => {
	const tableData = useMemo(() => getTableData(node), [node]);
	const sortFilterTableData = tableData?.sortFilter?.enabled ? tableData : null;
	const canSort = useMemo(() => canSortTable(node), [node]);
	const saved = useMemo(() => getSaved(node), [node]);

	const [activeFilter, setActiveFilter] = useState<FilterState>(saved.filter ?? {});
	const [activeSort, setActiveSort] = useState<SortRecord>(saved.sort ?? {});
	const [sortingOrder, setSortingOrder] = useState<number[]>(saved.sortingOrder ?? []);

	// tiptap keeps the NodeView when another article's table lands at the same position,
	// so the state is resynced whenever the saved sort/filter changes, not only the column count
	const savedKey = JSON.stringify(saved);

	// biome-ignore lint/correctness/useExhaustiveDependencies: savedKey stands for saved
	useLayoutEffect(() => {
		setActiveFilter((prev) => keepIfEqual(prev, saved.filter ?? {}));
		setActiveSort((prev) => keepIfEqual(prev, saved.sort ?? {}));
		setSortingOrder((prev) => keepIfEqual(prev, saved.sortingOrder ?? []));
	}, [savedKey, sortFilterTableData?.numCols]);

	const sorted = useMemo(() => hasActiveEntries(activeSort), [activeSort]);
	const active = useMemo(
		() => ({ filter: activeFilter, sort: activeSort, sortingOrder }),
		[activeFilter, activeSort, sortingOrder],
	);

	const saveSortAndFilterCallback = useCallback(
		(newSortFilter: FilterAndSort) => {
			saveSortAndFilter(editor, pos, { filter: activeFilter, sort: activeSort, sortingOrder, ...newSortFilter });
		},
		[editor, pos, activeFilter, activeSort, sortingOrder],
	);

	const onSortChange = useCallback(
		(colIndex: number, sortState: SortState) => {
			const next = { ...activeSort };
			let newSortingOrder = [...sortingOrder];
			if (sortState) {
				next[colIndex] = sortState;

				if (!newSortingOrder.includes(colIndex)) newSortingOrder.push(colIndex);
			} else {
				delete next[colIndex];
				newSortingOrder = newSortingOrder.filter((order) => order !== colIndex);
			}
			setActiveSort(next);
			setSortingOrder(newSortingOrder);

			const newSortFilter = { sort: next, sortingOrder: newSortingOrder } as FilterAndSort;
			saveSortAndFilterCallback(newSortFilter);
		},
		[saveSortAndFilterCallback, activeSort, sortingOrder],
	);

	const sortRowsCallback = useCallback(
		() => sortRows(sortFilterTableData, activeSort, sortingOrder, editor, pos),
		[sortFilterTableData, activeSort, sortingOrder, editor, pos],
	);
	const restorecallback = useCallback(() => restoreOrder(editor, pos), [editor, pos]);

	useWatchSort(sortFilterTableData, activeSort, sortRowsCallback, restorecallback);

	const columnsValues = getColumnsValues(sortFilterTableData);

	const onFilterChange = useCallback(
		(colIndex: number, excluded: string[]) => {
			const newFilter = getFilterChange(colIndex, excluded, activeFilter);
			setActiveFilter(newFilter);

			const newSortFilter = { filter: newFilter } as FilterAndSort;
			saveSortAndFilterCallback(newSortFilter);
		},
		[saveSortAndFilterCallback, activeFilter],
	);

	return useMemo(
		() => ({
			tableData: sortFilterTableData,
			aggregation: tableData?.aggregation,
			canSort,
			active,
			saved,
			onFilterChange,
			onSortChange,
			sorted,
			columnsValues,
		}),
		[
			sortFilterTableData,
			tableData?.aggregation,
			canSort,
			active,
			saved,
			onFilterChange,
			onSortChange,
			sorted,
			columnsValues,
		],
	);
};

export default useFilterAndSort;
